
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'

import {
  DealType,
  DealStatus,
  DiscountType,
  TextFieldRules,
  DealStatusText,
  DealTypeText,
  DiscountTypeText,
  RedemptionType,
  RedemptionTypeText,
  CompanyType
} from '@/utils/enums'
import { Deal } from '@/types/Deal'
import dealModule from '@/store/deal'
import authModule from '@/store/auth'
import venueModule from '@/store/venue'
import companyModule from '@/store/company'
import whitelabelModule from '@/store/whitelabel'
import axios from 'axios'
import { compareNested, uploadImage } from '@/utils/utility-manager'
import { format } from 'date-fns'
import WysiwygEditor from '@/components/WysiwygEditor.vue'

@Component({
  components: { WysiwygEditor },
  name: 'DealEditor'
})
class DealEditor extends Vue {
  @Prop() readonly edit?: boolean
  ready = false
  rules = TextFieldRules

  couponRule = (retailPrice: number, couponPrice: number): boolean | string => {
    return (
      (couponPrice / retailPrice) * 100 - 100 >= 25 ||
      'Der angegebene Rabatt ist nicht ausreichend. Bitte wähle deinen Gutscheinverkaufspreis & und den Gutscheinwert so aus, dass mindestens ein Rabatt von 25% erreicht wird'
    )
  }

  showStartDate = false
  showEndDate = false
  showValidityDate = false
  discountType = DiscountType
  categories: any[] = []
  tags: any[] = []
  currentImage = ''
  redemptionCodes: { single: string; multi: string } = {
    single: '',
    multi: ''
  }
  form: Omit<Deal, '_id' | 'whitelabel' | 'redemptionCodes'> = {
    name: '',
    type: '',
    description: '',
    descriptionTitle: 'Über diesen Deal',
    description2: '',
    description2Title: '',
    description3: '',
    description3Title: '',
    ctaTitle: '',
    status: DealStatus.DRAFT,
    startDate: '',
    endDate: '',
    validityDate: '',
    condition: '',
    conditionTitle: 'Konditionen',
    discount: {
      type: '',
      oldPrice: 0,
      newPrice: 0,
      relief: 0,
      minSum: 0,
      percentDiscount: 0,
      freeItems: '',
      onTopGoodie: '',
      couponValue: [
        {
          retailPrice: 1,
          couponPrice: 1.25,
          active: true
        }
      ]
    },
    categories: [],
    tags: [],
    venues: [],
    referralUrl: '',
    totalVoucher: 0,
    voucherPP: 1,
    image: '',
    media: [],
    company: '',
    exclusive: false,
    global: false,
    showInSearch: true,
    redemptionType: '',
    rewardLevel: 0,
    rewardPoints: 1,
    skus: []
  }

  get skusString(): string {
    return this.form.skus.join('\n')
  }

  set skusString(newVal: string) {
    this.form.skus = newVal.split('\n')
  }

  get deal() {
    return dealModule.deal!
  }

  get auth() {
    return authModule
  }

  get whitelabel() {
    return whitelabelModule.whitelabel
  }

  get whitelabels() {
    return whitelabelModule.whitelabels
  }

  get venues() {
    return venueModule.venues
  }

  get dealHasImage() {
    return this.form.image && this.form.image.length > 0
  }

  get dealMedia() {
    return this.form.media
  }

  get formattedEndDate() {
    if (this.form.endDate!.length === 0) return ''
    return format(new Date(this.form.endDate!), 'dd.MM.yyyy')
  }

  get formattedStartDate() {
    if (this.form.startDate!.length === 0) return ''
    return format(new Date(this.form.startDate!), 'dd.MM.yyyy')
  }

  get formattedValidityDate() {
    if (this.form.validityDate!.length === 0) return ''
    return format(new Date(this.form.validityDate!), 'dd.MM.yyyy')
  }

  get formCompany() {
    return this.form.company || ''
  }

  set formCompany(id: string) {
    Vue.set(this.form, 'company', id)
  }

  get relatedCompanies() {
    return companyModule.companies.filter(
      company =>
        company.whitelabel === this.whitelabel._id &&
        company.type!.includes(this.form.type!)
    )
  }

  get relatedVenues() {
    if (this.form.global) {
      return this.venues
    }
    if (authModule.isPartner) {
      return this.venues.filter(v =>
        companyModule.company!.venue.includes(v._id)
      )
    }

    const currentCompany = this.relatedCompanies.find(
      company => company._id === this.form.company
    )

    if (!currentCompany) return []
    return this.venues.filter(v => currentCompany.venue!.includes(v._id))
  }

  get status() {
    let entries = Object.entries(DealStatus)

    if (
      (!this.deal && authModule.isPartner) ||
      (this.deal &&
        authModule.isPartner &&
        (this.deal.status === DealStatus.DRAFT ||
          this.deal.status === DealStatus.REVIEW_PENDING))
    ) {
      entries = Object.entries(DealStatus).filter(
        ([, status]) =>
          status === DealStatus.DRAFT || status === DealStatus.REVIEW_PENDING
      )
    }

    return entries.map(([key, val]) => [DealStatusText[key], val])
  }

  get dealTypes() {
    return dealModule.dealTypes
  }

  get redemptionTypes() {
    if (this.form.type! === DealType.REWARDS) {
      return Object.entries(RedemptionType)
        .filter(
          el =>
            el[1] === RedemptionType.SINGLE_OUT ||
            el[1] === RedemptionType.MULTI_OUT
        )
        .map(entry => [RedemptionTypeText[entry[0]], entry[1]])
    }
    if (this.form.type! === DealType.ECOMMERCE) {
      return Object.entries(RedemptionType)
        .filter(
          el =>
            el[1] === RedemptionType.SINGLE_OUT ||
            el[1] === RedemptionType.MULTI_OUT ||
            el[1] === RedemptionType.NONE
        )
        .map(entry => [RedemptionTypeText[entry[0]], entry[1]])
    }
    if (this.form.type! === DealType.LOCAL) {
      return Object.entries(RedemptionType)
        .filter(
          el =>
            el[1] === RedemptionType.SINGLE_IN ||
            el[1] === RedemptionType.SINGLE_OUT ||
            el[1] === RedemptionType.MULTI_IN ||
            el[1] === RedemptionType.MULTI_OUT
        )
        .map(entry => [RedemptionTypeText[entry[0]], entry[1]])
    }
    return []
  }

  get discountTypes() {
    if (
      authModule.isPartner &&
      companyModule.company?.type.length === 1 &&
      companyModule.company?.type.includes(CompanyType.LOCAL)
    ) {
      return [[DiscountTypeText.COUPON, DiscountType.COUPON]]
    }
    return Object.entries(DiscountType).map(entry => [
      DiscountTypeText[entry[0]],
      entry[1]
    ])
  }

  get isDraft(): boolean {
    return this.form.status === DealStatus.DRAFT
  }

  get isLocal(): boolean {
    return this.form.type === DealType.LOCAL
  }

  get isReward(): boolean {
    return this.form.type === DealType.REWARDS
  }

  get isMulti(): boolean {
    return (
      this.form.redemptionType === RedemptionType.MULTI_IN ||
      this.form.redemptionType === RedemptionType.MULTI_OUT
    )
  }

  get disableMulti(): boolean {
    return (
      !!this.edit &&
      this.redemptionCodes.multi.length > 0 &&
      this.deal.voucherUsed > 0 &&
      this.isMulti
    )
  }

  get isSingle(): boolean {
    return (
      this.form.redemptionType === RedemptionType.SINGLE_IN ||
      this.form.redemptionType === RedemptionType.SINGLE_OUT
    )
  }

  get isReadonly(): boolean {
    return authModule.isPartner && this.form.status !== DealStatus.DRAFT
  }

  get companyNoDataText(): string {
    if (this.form.type!.length === 0) {
      return 'Wählen Sie einen Typ aus'
    } else {
      return 'Keine Unternehmen für diesen Typ verfügbar'
    }
  }

  get filteredCategories() {
    if (this.form.type! === DealType.REWARDS) {
      return this.categories
    }
    if (this.form.type! === DealType.LOCAL) {
      return this.categories.filter(el => el.type === DealType.LOCAL)
    }
    if (this.form.type! === DealType.ECOMMERCE) {
      return this.categories.filter(el => el.type === DealType.ECOMMERCE)
    }
    return []
  }

  get isNotAdmin() {
    return !(authModule.isSuperadmin || authModule.isAdmin)
  }

  get discountTypeAndDealType(): [string | undefined, string | undefined] {
    return [this.form.discount?.type, this.form.type]
  }

  @Watch('form.type')
  setRewardPoints(): void {
    if (this.form.type === DealType.LOCAL) {
      this.form.rewardPoints = this.whitelabel.settings.localRedemptionPoints
    } else if (this.form.type === DealType.ECOMMERCE) {
      this.form.rewardPoints = this.whitelabel.settings.ecommerceRedemptionPoints
    } else {
      this.form.rewardPoints = 1
    }
  }

  getDefaultCtaTitle(
    discountType: string | undefined,
    type: string | undefined
  ): string {
    if (discountType === DiscountType.INFO) return 'Mehr dazu'
    if (discountType === DiscountType.COUPON) return 'Gutschein kaufen'

    return 'Einlösen'
  }

  @Watch('discountTypeAndDealType')
  setCtaTitle(
    [discountType, dealType]: [string | undefined, string | undefined],
    [prevDiscountType, prevDealType]: [string | undefined, string | undefined]
  ): void {
    const prevDefaultCtaTitle = this.getDefaultCtaTitle(
      prevDiscountType,
      prevDealType
    )

    if (this.form.ctaTitle && prevDefaultCtaTitle !== this.form.ctaTitle) return

    this.form.ctaTitle = this.getDefaultCtaTitle(discountType, dealType)
  }

  async created(): Promise<void> {
    if (authModule.isSuperadmin || authModule.isAdmin) {
      await companyModule.fetchAll({ limit: 0, lean: true })
    }
    await venueModule.fetchAll({ limit: 0, lean: true })

    const { data } = await axios.get(
      `/admin/${this.auth.user!.whitelabel.slug}/category/list`
    )
    this.categories = data.categories

    const { data: tagdata } = await axios.get(
      `/admin/${this.auth.user!.whitelabel.slug}/tag/list`
    )
    this.tags = tagdata.tags

    if (authModule.isPartner || authModule.isLightPartner) {
      Object.assign(this.form, { company: companyModule.company!._id })
    }

    this.ready = true

    if (!this.edit) return

    await dealModule.fetch(this.$route.params.did)

    this.form = JSON.parse(JSON.stringify(this.deal))
    if (this.form.redemptionCodes) {
      if (this.isMulti) {
        this.redemptionCodes.multi = this.form.redemptionCodes.toString()
      } else {
        this.redemptionCodes.single = this.form.redemptionCodes.toString()
      }
    }
    if (this.form.venues && this.form.venues.length > 0) {
      const tmpVenues = []

      for (const venueID of this.form.venues) {
        const tmpVenue = this.relatedVenues.find(el => el._id === venueID)
        if (tmpVenue) {
          tmpVenues.push(tmpVenue)
        }
      }
      Object.assign(this.form, { venues: tmpVenues })
    }
    if (!this.form.rewardPoints || this.form.rewardPoints === 0) {
      this.setRewardPoints()
    }
    if (this.form.image) {
      this.currentImage = this.form.image!
    }
    if (this.form.discount?.couponValue?.length === 0) {
      this.form.discount.couponValue.push({
        retailPrice: 0,
        couponPrice: 0,
        active: true
      })
    }
  }

  parseCSVCodes(file: File): void {
    const reader = new FileReader()
    reader.addEventListener('load', (event: any) => {
      this.redemptionCodes.multi = event.target.result.replace(/\r\n|\n/gm, ',')
    })
    reader.readAsText(file)
  }

  async addImage(file: File): Promise<void> {
    if (!file || !this.isImage(file) || file.size > 4194304) {
      this.resetImage()
      return
    }
    const result = await uploadImage(file)

    this.form.image = result[0].key
    this.currentImage = result[0].url
  }

  resetImage(): void {
    this.form.image = undefined
  }

  async addMedia(file: File | File[]): Promise<void> {
    const fileArr = file instanceof File ? [file] : file
    if (
      !file ||
      fileArr.length === 0 ||
      this.form.media!.length > 5 ||
      fileArr.some(el => el.size > 4194304)
    ) {
      this.form.media = []
      return
    }

    if (file instanceof File) {
      if (!this.isImage(file)) return
    } else {
      for (const media of file) {
        if (!this.isImage(media)) return
      }
    }
    const result = await uploadImage(file)

    for (const item of result) {
      this.form.media!.push({ type: 'image', url: item.key })
    }
  }

  removeMedia(index: number): void {
    if (!this.form.media![index]) return
    this.form.media!.splice(index, 1)
  }

  isImage(file: File): boolean {
    if (!file) return false
    return file.type.split('/')[0] === 'image'
  }

  async saveDeal(): Promise<void> {
    if (this.isMulti) {
      const matches = this.redemptionCodes.multi.match(
        /[a-zA-Z0-9!@#$%^&*)(+=._-]+/gm
      )
      if (matches) {
        this.form.totalVoucher = matches.length
        Object.assign(this.form, { redemptionCodes: matches })
      }
    } else if (this.isSingle) {
      Object.assign(this.form, {
        redemptionCodes: [this.redemptionCodes.single]
      })
    } else if (this.form.redemptionType === RedemptionType.NONE) {
      Object.assign(this.form, { redemptionCodes: [] })
    }
    if (!this.edit) {
      dealModule.create({
        whitelabel: this.whitelabel._id,
        company: this.form.company!,
        deal: this.form
      })
    } else {
      const payload: Partial<Deal> = await compareNested(this.form, this.deal)
      if (payload.media) {
        payload.media = payload.media.map(el => {
          return { type: el.type, url: el.url.split('/').pop()! }
        })
      }
      Object.assign(payload, { _id: this.deal!._id })
      dealModule.update({ whitelabel: this.deal.whitelabel, deal: payload })
    }
    document.dispatchEvent(new Event('close-deal'))
  }

  setActiveCouponValue(index: number): void {
    if (!this.form.discount?.couponValue) return

    this.form.discount.couponValue.forEach(el => {
      el.active = false
    })

    this.$nextTick(() => {
      this.form.discount!.couponValue![index].active = true
    })
  }

  addCouponValue(): void {
    if (!this.form.discount!.couponValue) {
      Object.assign(this.form.discount, { couponValue: [] })
    }

    const isOnlyValue = this.form.discount!.couponValue!.length === 0

    this.form.discount!.couponValue!.push({
      retailPrice: 1,
      couponPrice: 1.25,
      active: isOnlyValue
    })
  }

  removeCouponValue(index: number): void {
    this.form.discount!.couponValue!.splice(index, 1)
  }

  validateCouponForm(index: number): void {
    const form = this.$refs[`couponValueForm-${index}`] as any
    form[0].validate()
  }
}

export default DealEditor
